import React from "react";
import { clsx } from "clsx";
import {
    CheckCircleFilled,
    CloseCircleFilled,
    CloseOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import { Tooltip } from "../../../design-system";
import { DocState, docTypeLabel, requirementGroupLabel, unsatisfiedGroups } from "../useGuidedIntake";
import type { Document, GuidedIntakeState } from "../useGuidedIntake";
import type { ProjectType } from "../../../types/ProjectTypes";
import classes from "../GuidedIntake.module.less";

/**
 * Width-aware middle truncation. Sibling uploads often share a long prefix
 * ("CON-23004881-Takeda-Renovo-MSA.pdf" vs "...-SOW.pdf"), so an end ellipsis
 * makes them indistinguishable. The name is split into a head and a pinned
 * tail (which carries the discriminating suffix and the extension): the head
 * ellipsizes to whatever width is actually available, the tail always shows.
 */
const TAIL_CHARS = 14;
const DocLabel: React.FC<{ name: string }> = ({ name }) =>
    name.length <= TAIL_CHARS ? (
        <span className={classes.docRowLabel}>{name}</span>
    ) : (
        <span className={classes.docRowLabel}>
            <span className={classes.docRowLabelHead}>{name.slice(0, -TAIL_CHARS)}</span>
            <span className={classes.docRowLabelTail}>{name.slice(-TAIL_CHARS)}</span>
        </span>
    );

const DocRow: React.FC<{
    doc: Document;
    projectType: ProjectType | null;
    onDelete?: (doc: Document) => void;
}> = ({ doc, projectType, onDelete }) => {
    const isQueued = doc.state === DocState.Queued;
    const isParsing = doc.state === DocState.Parsing;
    const isReady = doc.state === DocState.Ready;
    const isFailed = doc.state === DocState.Failed;
    const isPending = isQueued || isParsing;

    const statusText = isQueued
        ? "Waiting…"
        : isParsing
        ? "Reading content…"
        : isFailed
        ? doc.error ?? "Couldn't add this file."
        : "";

    return (
        <div className={clsx(classes.docRow, isReady && classes.docRowReady)}>
            <div className={classes.docRowCheck}>
                {isParsing ? (
                    <LoadingOutlined className={classes.docRowSpinner} />
                ) : isReady ? (
                    <CheckCircleFilled className={classes.docRowCheckReady} />
                ) : isFailed ? (
                    <CloseCircleFilled className={classes.docRowCheckFailed} />
                ) : (
                    <div className={classes.docRowCheckbox} />
                )}
            </div>
            <div className={classes.docRowBody}>
                <div className={classes.docRowLabelLine}>
                    <Tooltip
                        overlayStyle={{ maxWidth: "none" }}
                        title={doc.label.length > TAIL_CHARS ? doc.label : undefined}
                    >
                        <DocLabel name={doc.label} />
                    </Tooltip>
                    {isReady && doc.size && (
                        <span className={classes.docRowSize}>{doc.size}</span>
                    )}
                </div>
                {isReady && doc.types && doc.types.length > 0 && (
                    <div className={classes.docRowTypes}>
                        {doc.types.map((type) => (
                            <span key={type} className={classes.docRowTagDetected}>
                                {docTypeLabel(type, projectType).toUpperCase()}
                            </span>
                        ))}
                    </div>
                )}
                {statusText && (
                    <div className={clsx(classes.docRowStatus, isFailed && classes.docRowStatusError)}>
                        {statusText}
                    </div>
                )}
            </div>
            {onDelete && !isPending && (
                <button
                    type="button"
                    className={classes.docRowDelete}
                    onClick={() => onDelete(doc)}
                    aria-label={`Remove ${doc.label}`}
                >
                    <CloseOutlined />
                </button>
            )}
        </div>
    );
};

/**
 * The Documents list: uploaded documents and the still-missing requirements in
 * one list, plus a tooltip listing other document types worth uploading.
 *
 * A missing row is an unsatisfied requirement group, shown by its label and
 * ticked off by a matching upload OR a covered topic.
 */
export const Documents: React.FC<{
    state: GuidedIntakeState;
    onDelete?: (doc: Document) => void;
}> = ({ state, onDelete }) => {
    const { documents, documentGroups, topics, projectType } = state;
    const suggested = state.suggestedDocumentTypes;
    const missingGroups = unsatisfiedGroups(documentGroups, documents, topics);
    // Before the kind is picked there are no expected-document rows either
    // (the kind's requirement groups arrive with the draft), so show a hint.
    if (documents.length === 0 && documentGroups.length === 0) {
        return (
            <p className={classes.emptyHint}>The documents you upload will show up here.</p>
        );
    }
    return (
        <div className={classes.docList}>
            {documents.map((document) => (
                <DocRow
                    key={document.id}
                    doc={document}
                    projectType={projectType}
                    onDelete={onDelete}
                />
            ))}
            {missingGroups.map((group) => (
                <div key={`missing-${group.id}`} className={classes.requiredDocRow}>
                    <span className={classes.requiredDocCheck} />
                    <span className={classes.requiredDocLabel}>
                        {requirementGroupLabel(group, projectType)}
                    </span>
                </div>
            ))}

            {suggested.length > 0 && (
                <Tooltip
                    placement="bottom"
                    overlayStyle={{ maxWidth: "none" }}
                    title={
                        <ul className={classes.suggestedDocsList}>
                            {suggested.map((type) => (
                                <li key={type}>{type}</li>
                            ))}
                        </ul>
                    }
                >
                    <span className={classes.suggestedDocsHint}>
                        Other suggested document types
                    </span>
                </Tooltip>
            )}
        </div>
    );
};

export default Documents;
